// covered-by: qa/rickrubin/tests/specs/cca/knowledge.database.spec.ts
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import KnowledgeDatabaseView from "./view";

import PendingComponent from "../../../../components/PendingComponent";
import {
  canAccessKnowledgeDatabase,
  requiresAuth,
} from "../../../../utils/auth";
import { KnowledgeRuleCategory } from "../../../../utils/parsers/knowledge";
import { authLayoutRoute } from "../../../auth-layout/route";
import { ticketsRoute } from "../../../tickets/route";
// TODO: move this to app-wide components
import ErrorComponent from "../../../tickets/routes/ticket/components/ErrorComponent";

/**
 * Sentinel `tab` value for the read-only enterprise-memories tab. Pinned here
 * so the route schema, the Tabs.Trigger, and the panel all agree on the exact
 * string — Radix Tabs is controlled, so a mismatch silently blanks the panel.
 */
export const MEMORIES_TAB = "memories";

export const KNOWLEDGE_TABS = [
  "sources",
  "rules",
  MEMORIES_TAB,
  "activity",
] as const;
export type KnowledgeTab = (typeof KNOWLEDGE_TABS)[number];

export const RULE_STATUS_FILTERS = [
  "active",
  "suggestions",
  "archived",
] as const;
export type RuleStatusFilter = (typeof RULE_STATUS_FILTERS)[number];

/**
 * Canonical landing search for cross-route links into the Knowledge Database.
 * `tab`/`status` use `.catch(...)` so their OUTPUT type is required — a bare
 * `{ tab: "rules" }` literal fails the router's Link typing. Every external
 * Link/Navigate into this route should spread this object.
 */
export const KNOWLEDGE_DATABASE_DEFAULT_SEARCH = {
  tab: "sources",
  status: "active",
} as const satisfies { tab: KnowledgeTab; status: RuleStatusFilter };

export const knowledgeDatabaseRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  validateSearch(search: Record<string, unknown>) {
    // `.catch(...)` (not strict enums) so stale legacy deep links — the old
    // dynamic per-model tabs used `?tab=<modelId>&pageSize=100` — land on
    // Sources instead of 404ing.
    const schema = z.object({
      tab: z.enum(KNOWLEDGE_TABS).catch("sources"),
      status: z.enum(RULE_STATUS_FILTERS).catch("active"),
      // Single-category filter for the Rules tab; omitted = all categories.
      category: z.nativeEnum(KnowledgeRuleCategory).optional().catch(undefined),
      q: z.string().optional().catch(undefined),
      ruleId: z.string().optional().catch(undefined),
      sourceId: z.string().optional().catch(undefined),
    });

    return schema.parse(search);
  },
  path: "settings/knowledge-database",
  beforeLoad() {
    requiresAuth();

    if (!canAccessKnowledgeDatabase()) {
      throw redirect({
        to: ticketsRoute.to,
      });
    }
  },
  component: KnowledgeDatabaseView,
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
  gcTime: 0,
  shouldReload: false,
  pendingMs: 1000,
  pendingMinMs: 500,
});
